import React from 'react';
import { useTranslation } from 'react-i18next';
import styles from './accountDisconectionPage.module.scss';
import PermissionClusterDescriptions from '../../constants/PermissionClustersDescriptions';
import PermissionCluster from '../../domain/enums/PermissionCluster';
import Link from '../../components/links/Link';
import getReplacementTokens from '../../utils/GetReplacementTokens';
import { useErrorState, useSelectedBank } from '../../hooks/zustandHooks';
import { useConsentConfiguration, useConsentInfo } from '../../hooks/queryHooks';
import { useHistory } from 'react-router';
import { accessTokenStorage } from '../../domain/services';
import { useRevokeConsentMutation } from '../../hooks/mutationHooks';
import ErrorScreenTypeEnum from '../../types/enums/errorScreenTypeEnum';
import Loader from '../../components/loader/Loader';
import Container from '../../components/container/Container';
import CheckSquare from '../../assets/CheckSquare';
import { LogoHeader } from '../../components/consent/LogoHeader';

interface IItem {
  title: string;
  content: string[];
}

const AccountDisconnectionPage = () => {
  const history = useHistory();
  const { consentInfo } = useConsentInfo();
  const setError = useErrorState(state => state.setError);
  const { consentConfiguration } = useConsentConfiguration();
  const replacementTokens = getReplacementTokens(consentConfiguration);
  const bankLogoUrl = useSelectedBank(state => state.selectedBank?.logoUrl);
  const { t } = useTranslation();
  const { mutateAsync, isPending } = useRevokeConsentMutation();
  const items: IItem[] = [];
  const permissionClusters = [
    PermissionCluster.AccountDetails,
    PermissionCluster.Balances,
    PermissionCluster.Transactions
  ];

  permissionClusters.forEach(permissionCluster => {
    const permission = PermissionClusterDescriptions.get(permissionCluster);
    if (permission) {
      items.push({
        title: t(permission.title),
        content: permission.content.map(contentPart => t(contentPart, replacementTokens))
      });
    }
  });

  const onClickBack = () => {
    if (!accessTokenStorage.hasToken()) {
      setError({
        type: ErrorScreenTypeEnum.ReconfirmationError,
        code: 'no access token could be found.'
      });
      return;
    }

    if (!consentInfo) {
      setError({
        type: ErrorScreenTypeEnum.ReconfirmationError,
        code: 'no consent information could be found.'
      });
      return;
    }

    history.push('/reconfirmation-stage');
  };

  if (isPending) {
    return <Loader display={true} spinnerText="Revoking" />;
  }

  return (
    <Container
      data-testid="account-disconnection-page"
      primaryBtn={{
        text: t('Disconnect'),
        className: styles.errorButton,
        onClick: async () => await mutateAsync()
      }}
      secondaryBtn={{
        text: t('Back'),
        onClick: onClickBack
      }}
    >
      <LogoHeader bankLogoUrl={bankLogoUrl} bankName={consentInfo?.providerName} />
      <div className="mb-6">
        <h1 className="text-md md:text-lg text-primary-2 uppercase font-semi-bold mb-3">
          {t<string>('Disconnect') + ' ' + consentInfo?.providerName}
        </h1>
        <p className={`text-md md:text-lg text-primary-2`}>
          {t<string>(
            'You are about to disconnect your account, so we will no longer be able to get data from the account.'
          )}
        </p>
      </div>
      <p className="mb-6">{t<string>('Data we can currently get:')}</p>
      <div className="mb-6">
        {items.map(item => {
          return (
            <div key={item.title}>
              {item.content.map(content => {
                return (
                  <div key={content} className="flex gap-2 mb-4">
                    <CheckSquare />
                    {content}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
      <div className="privacy-policy-section">
        <p className={styles.paragraph}>
          {t<string>('Data disclaimer. Learn more about our ')}
          <Link href={'https://www.atto.co/policies'} target="_blank">
            {t<string>('Privacy Policy')}
          </Link>
        </p>
      </div>
    </Container>
  );
};

export { AccountDisconnectionPage };
export default AccountDisconnectionPage;
